import type { AuthUser } from '@/services/auth'

export type UserRole = 'admin' | 'user' | 'viewer' | 'no_access'
export type PageAccess = 'edit' | 'read' | 'hidden'

export const SUPER_ADMIN_EMAIL = 'tcrhyne@gmail.com'

export const PAGE_KEYS = [
  'invoices',
  'purchasing',
  'vendors',
  'transactions',
  'gcp',
  'gift-cards',
  'clover-reports',
  'warehouse',
  'payroll',
  'benefits',
  'fixed-assets',
  'bank-recon',
  'member-ledger',
  'hp-threshold',
  'preferred-equity',
  'distributions',
  'monthly-performance',
  'projections',
  'settings',
] as const

export type PageKey = (typeof PAGE_KEYS)[number]

const PATH_PREFIX_TO_PAGE_KEY: Array<[string, PageKey]> = [
  ['/bookkeeper', 'invoices'],
  ['/invoice-minion', 'invoices'],
  ['/member-ledger', 'member-ledger'],
  ['/distribution-waterfall', 'distributions'],
  ['/hp-threshold', 'hp-threshold'],
  ['/projections', 'projections'],
  ['/gcp', 'gcp'],
  ['/reporting', 'monthly-performance'],
  ['/clover', 'clover-reports'],
  ['/gift-cards', 'gift-cards'],
  ['/bank-recon', 'bank-recon'],
  ['/warehouse', 'warehouse'],
  ['/purchasing', 'purchasing'],
  ['/payroll', 'payroll'],
  ['/benefits', 'benefits'],
  ['/preferred-equity', 'preferred-equity'],
  ['/fixed-assets', 'fixed-assets'],
  ['/qbo-transaction-search', 'transactions'],
  ['/vendor-details', 'vendors'],
  ['/settings', 'settings'],
]

export const PAGE_KEY_DEFAULT_PATH: Record<PageKey, string> = {
  invoices: '/bookkeeper/doc-review',
  'member-ledger': '/member-ledger/unit-ledger',
  distributions: '/distribution-waterfall/calculator',
  'hp-threshold': '/hp-threshold/balances',
  projections: '/projections',
  gcp: '/gcp',
  'monthly-performance': '/reporting/report',
  'clover-reports': '/clover',
  'bank-recon': '/bank-recon',
  warehouse: '/warehouse',
  purchasing: '/purchasing/review',
  'gift-cards': '/gift-cards',
  payroll: '/payroll',
  benefits: '/benefits',
  'preferred-equity': '/preferred-equity',
  'fixed-assets': '/fixed-assets/allocate',
  transactions: '/qbo-transaction-search',
  vendors: '/vendor-details',
  settings: '/settings',
}

export function isSuperAdminEmail(email: string): boolean {
  return email.trim().toLowerCase() === SUPER_ADMIN_EMAIL
}

export function pageKeyFromPath(path: string): PageKey | null {
  const normalized = path.startsWith('/') ? path : `/${path}`
  for (const [prefix, pageKey] of PATH_PREFIX_TO_PAGE_KEY) {
    if (normalized === prefix || normalized.startsWith(`${prefix}/`)) {
      return pageKey
    }
  }
  return null
}

export function getAccess(user: AuthUser | null, pageKey: PageKey): PageAccess {
  if (!user?.permissions) {
    return 'hidden'
  }
  const access = user.permissions[pageKey]
  if (access === 'edit' || access === 'read' || access === 'hidden') {
    return access
  }
  return 'hidden'
}

export function canView(user: AuthUser | null, pageKey: PageKey): boolean {
  const access = getAccess(user, pageKey)
  return access === 'edit' || access === 'read'
}

export function canEdit(user: AuthUser | null, pageKey: PageKey): boolean {
  return getAccess(user, pageKey) === 'edit'
}

export function isAdmin(user: AuthUser | null): boolean {
  return user?.role === 'admin'
}

export function getFirstVisiblePagePath(user: AuthUser | null): string | null {
  for (const pageKey of PAGE_KEYS) {
    if (canView(user, pageKey)) {
      return PAGE_KEY_DEFAULT_PATH[pageKey]
    }
  }
  return null
}

export const ACCESS_OPTIONS: Array<{ value: PageAccess; label: string }> = [
  { value: 'edit', label: 'Edit' },
  { value: 'read', label: 'Read-only' },
  { value: 'hidden', label: 'Hidden' },
]

export const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'no_access', label: 'No access' },
]
